import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';

export interface LoadConfig {
  id?: number;
  width: number;
  depth: number;
  height: number;
  unit?: string;
  quantityLimit: number;
  weight: number;
}

type NewLoadConfig = Omit<LoadConfig, 'id'>;

type AddLoadConfigPayload = {
  data: NewLoadConfig;
  pageNo: number;
  pageSize: number;
};

type ThunkArgs = {
  id: number;
  updatePayload: {
    quantityLimit: number;
    radiusLimit: number;
    width: number;
    depth: number;
    height: number;
    weight: number;
    unit: string;
  };
  pageNo: number;
  pageSize: number;
};

type GetLoadsConfigParams = {
  pageNo: number;
  pageSize: number;
  orderIDs?: string[];
  loadConfigID?: number;
  search?: string;
};

type initialStateProps = {
  isLoading: {
    addLoading: boolean;
    updateLoading: boolean;
    deleteLoading: boolean;
    deactivateLoading: boolean;
    getLoading: boolean;
  };
  type: 'loads' | 'add-load' | 'update-load' | 'delete-load';
  data: LoadConfig[];
  error: string | null;
  count: number | null;
};

const initialState: initialStateProps = {
  isLoading: {
    addLoading: false,
    updateLoading: false,
    deleteLoading: false,
    deactivateLoading: false,
    getLoading: false
  },
  type: 'loads',
  data: [],
  error: null,
  count: null
};

// Fetch load configurations
export const getLoadsConfigAction = createAsyncThunk<any, GetLoadsConfigParams>(
  'loadConfig/all',
  async (data, thunkApi) => {
    const { pageNo, pageSize, orderIDs = [], loadConfigID, search } = data;
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.GET_LOAD_CONFIG}?pageNo=${pageNo}&pageSize=${pageSize}`
      });
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response?.data?.message || 'Failed to fetch load configurations',
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error.response?.data || error);
    }
  }
);

// Add load configuration
export const addLoadConfig = createAsyncThunk<any, AddLoadConfigPayload>(
  'loadConfig/add',
  async (params, thunkApi) => {
    const { data, pageNo, pageSize } = params;
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: URLs.ADD_LOAD_CONFIG,
        data: {
          quantityLimit: data.quantityLimit,
          radiusLimit: 100,
          width: data.width,
          depth: data.depth,
          height: data.height,
          weight: data.weight,
          unit: data.unit
        }
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message || 'Load configuration added successfully',
          severity: 'success'
        })
      );
      return response?.data?.data || response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response?.data?.message || 'Failed to add load configuration',
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error.response?.data || error);
    }
  }
);

// Update load configuration
export const updateLoadConfig = createAsyncThunk<any, ThunkArgs>(
  'loadConfig/update',
  async (data, thunkApi) => {
    const { id, updatePayload, pageNo, pageSize } = data;
    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${URLs.UPDATE_LOAD_CONFIG}?id=${id}`,
        data: updatePayload
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message:
            response?.data?.message || 'Load configuratiosdsdsdn updated successfully',
          severity: 'success'
        })
      );
      return response?.data?.data || response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response?.data?.message || 'Failed to update load configuration',
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error.response?.data || error);
    }
  }
);

// Delete load configuration
export const deleteLoadConfig = createAsyncThunk<
  number,
  { id: number; pageNo: number; pageSize: number }
>('loadConfig/delete', async (data, thunkApi) => {
  const { id, pageNo, pageSize } = data;
  try {
    const response = await axiosInstance({
      method: 'DELETE',
      url: `${URLs.DELETE_LOAD_CONFIG}?id=${id}`
    });
    thunkApi.dispatch(
      updateToast({
        show: true,
        message: response?.data?.message || 'Load configuration deleted successfully',
        severity: 'success'
      })
    );
    return id;
  } catch (error: any) {
    thunkApi.dispatch(
      updateToast({
        show: true,
        message: error.response?.data?.message || 'Failed to delete load configuration',
        severity: 'error'
      })
    );
    return thunkApi.rejectWithValue(error.response?.data || error);
  }
});

// Deactivate load configuration
export const deactivateLoadConfig = createAsyncThunk<
  number,
  { id: number; pageNo: number; pageSize: number }
>('loadConfig/deactivate', async (data, thunkApi) => {
  const { id, pageNo, pageSize } = data;
  try {
    const response = await axiosInstance({
      method: 'PATCH',
      url: `${URLs.DEACTIAVTE_LOAD_CONFIG}?Id=${id}`
    });
    thunkApi.dispatch(
      updateToast({
        show: true,
        message: response?.data?.message,
        severity: 'success'
      })
    );
    return id;
  } catch (error: any) {
    thunkApi.dispatch(
      updateToast({
        show: true,
        message: error.response?.data?.message || 'Failed to delete load configuration',
        severity: 'error'
      })
    );
    return thunkApi.rejectWithValue(error.response?.data || error);
  }
});

const loadConfigSlice = createSlice({
  name: 'loadConfig',
  initialState,
  reducers: {
    clearLoadsConfigAction: state => {
      state.data = [];
      state.error = null;
      state.count = null;
    }
  },
  extraReducers: builder => {
    // getLoadsConfigAction
    builder
      .addCase(getLoadsConfigAction.pending, state => {
        state.isLoading.getLoading = true;
        state.type = 'loads';
        state.error = null;
      })
      .addCase(getLoadsConfigAction.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading.getLoading = false;
        const { content, totalElements } = action.payload.data || {};
        state.data =
          content?.map((item: any) => ({
            ...item,
            id: item.id ?? null,
            width: String(item.width ?? '0'),
            depth: String(item.depth ?? '0'),
            height: String(item.height ?? '0'),
            unit: item.unit ?? 'cm',
            orderQuantity: String(item.quantityLimit ?? '0'),
            weight: String(item.weight ?? '0')
          })) || [];
        state.count = totalElements ?? 0;
        state.error = null;
      })
      .addCase(getLoadsConfigAction.rejected, (state, action: any) => {
        state.isLoading.getLoading = false;
        state.error = action.payload?.message || 'Failed to fetch load configurations';
      });

    // addLoadConfig
    builder
      .addCase(addLoadConfig.pending, state => {
        state.isLoading.addLoading = true;
        state.error = null;
      })
      .addCase(addLoadConfig.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading.addLoading = false;
        state.error = null;
      })
      .addCase(addLoadConfig.rejected, (state, action: any) => {
        state.isLoading.addLoading = false;
        state.error = action.payload?.message || 'Failed to add load configuration';
      });

    // updateLoadConfig
    builder
      .addCase(updateLoadConfig.pending, state => {
        state.isLoading.updateLoading = true;
        state.type = 'update-load';
        state.error = null;
      })
      .addCase(updateLoadConfig.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading.updateLoading = false;
        state.error = null;
      })
      .addCase(updateLoadConfig.rejected, (state, action: any) => {
        state.isLoading.updateLoading = false;
        state.error = action.payload?.message || 'Failed to update load configuration';
      });

    // deleteLoadConfig
    builder
      .addCase(deleteLoadConfig.pending, state => {
        state.isLoading.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteLoadConfig.fulfilled, (state, action: PayloadAction<number>) => {
        state.isLoading.deleteLoading = false;
        state.error = null;
      })
      .addCase(deleteLoadConfig.rejected, (state, action: any) => {
        state.isLoading.deleteLoading = false;
        state.error = action.payload?.message || 'Failed to delete load configuration';
      });

    // deactiavteLoadConfig
    builder
      .addCase(deactivateLoadConfig.pending, state => {
        state.isLoading.deactivateLoading = true;
        state.error = null;
      })
      .addCase(deactivateLoadConfig.fulfilled, (state, action: PayloadAction<number>) => {
        state.isLoading.deactivateLoading = false;
        state.error = null;
      })
      .addCase(deactivateLoadConfig.rejected, (state, action: any) => {
        state.isLoading.deactivateLoading = false;
        state.error = action.payload?.message || 'Failed to delete load configuration';
      });
  }
});

export const loadConfigReducer = loadConfigSlice.reducer;
export const { clearLoadsConfigAction } = loadConfigSlice.actions;
